/**
 * Rate Limiter Service
 * Prevents brute force attacks by limiting authentication attempts
 */

export interface RateLimitConfig {
  maxAttempts: number;
  windowMs: number;
  blockDurationMs: number;
}

export interface RateLimitResult {
  allowed: boolean;
  remainingAttempts: number;
  resetTime: number;
  isBlocked: boolean;
}

export class RateLimiter {
  private static instance: RateLimiter | null = null;
  private attempts: Map<string, number[]> = new Map();
  private blockedUntil: Map<string, number> = new Map();
  
  private readonly DEFAULT_CONFIG: RateLimitConfig = {
    maxAttempts: 5,
    windowMs: 15 * 60 * 1000, // 15 minutes
    blockDurationMs: 30 * 60 * 1000 // 30 minutes
  };

  static getInstance(): RateLimiter {
    if (!RateLimiter.instance) {
      RateLimiter.instance = new RateLimiter();
    }
    return RateLimiter.instance;
  }

  /**
   * Check if action is allowed for identifier
   */
  checkLimit(identifier: string, config?: Partial<RateLimitConfig>): RateLimitResult {
    const finalConfig = { ...this.DEFAULT_CONFIG, ...config };
    const now = Date.now();

    // Check if currently blocked
    const blockedUntil = this.blockedUntil.get(identifier);
    if (blockedUntil && now < blockedUntil) {
      return {
        allowed: false,
        remainingAttempts: 0,
        resetTime: blockedUntil,
        isBlocked: true
      };
    }

    // Clear block if expired
    if (blockedUntil && now >= blockedUntil) {
      this.blockedUntil.delete(identifier);
      this.attempts.delete(identifier);
    }

    // Get recent attempts
    const attempts = this.attempts.get(identifier) || [];
    const recentAttempts = attempts.filter(time => now - time < finalConfig.windowMs);

    // Check if limit exceeded
    if (recentAttempts.length >= finalConfig.maxAttempts) {
      // Block the identifier
      const blockUntil = now + finalConfig.blockDurationMs;
      this.blockedUntil.set(identifier, blockUntil);
      
      console.warn(`🚨 Rate limit exceeded for ${identifier}. Blocked until ${new Date(blockUntil).toISOString()}`);
      
      return {
        allowed: false,
        remainingAttempts: 0,
        resetTime: blockUntil,
        isBlocked: true
      };
    }

    // Record this attempt
    recentAttempts.push(now);
    this.attempts.set(identifier, recentAttempts);

    return {
      allowed: true,
      remainingAttempts: finalConfig.maxAttempts - recentAttempts.length,
      resetTime: now + finalConfig.windowMs,
      isBlocked: false
    };
  }

  /**
   * Record successful action (reset attempts)
   */
  recordSuccess(identifier: string): void {
    this.attempts.delete(identifier);
    this.blockedUntil.delete(identifier);
  }

  /**
   * Record failed action
   */
  recordFailure(identifier: string, config?: Partial<RateLimitConfig>): RateLimitResult {
    return this.checkLimit(identifier, config);
  }

  /**
   * Check if identifier is currently blocked
   */
  isBlocked(identifier: string): boolean {
    const blockedUntil = this.blockedUntil.get(identifier);
    if (!blockedUntil) return false;
    
    const now = Date.now();
    if (now >= blockedUntil) {
      this.blockedUntil.delete(identifier);
      return false;
    }
    
    return true;
  }

  /**
   * Get remaining attempts for identifier
   */
  getRemainingAttempts(identifier: string, config?: Partial<RateLimitConfig>): number {
    const finalConfig = { ...this.DEFAULT_CONFIG, ...config };
    const now = Date.now();
    
    if (this.isBlocked(identifier)) {
      return 0;
    }
    
    const attempts = this.attempts.get(identifier) || [];
    const recentAttempts = attempts.filter(time => now - time < finalConfig.windowMs);
    
    return Math.max(0, finalConfig.maxAttempts - recentAttempts.length);
  }

  /**
   * Get time until reset for identifier
   */
  getTimeUntilReset(identifier: string): number {
    const blockedUntil = this.blockedUntil.get(identifier);
    if (blockedUntil) {
      return Math.max(0, blockedUntil - Date.now());
    }
    
    const attempts = this.attempts.get(identifier);
    if (!attempts || attempts.length === 0) {
      return 0;
    }
    
    const oldestAttempt = Math.min(...attempts);
    const resetTime = oldestAttempt + this.DEFAULT_CONFIG.windowMs;
    
    return Math.max(0, resetTime - Date.now());
  }

  /**
   * Format time until reset
   */
  getTimeUntilResetFormatted(identifier: string): string {
    const ms = this.getTimeUntilReset(identifier);
    
    if (ms === 0) return 'Now';
    
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    
    if (hours > 0) return `${hours} hour${hours > 1 ? 's' : ''}`;
    if (minutes > 0) return `${minutes} minute${minutes > 1 ? 's' : ''}`;
    return `${seconds} second${seconds > 1 ? 's' : ''}`;
  }

  /**
   * Clear all rate limit data for identifier
   */
  clear(identifier: string): void {
    this.attempts.delete(identifier);
    this.blockedUntil.delete(identifier);
  }

  /**
   * Clear all rate limit data
   */
  clearAll(): void {
    this.attempts.clear();
    this.blockedUntil.clear();
  }

  /**
   * Get rate limit status for identifier
   */
  getStatus(identifier: string): {
    isBlocked: boolean;
    remainingAttempts: number;
    timeUntilReset: string;
    attempts: number;
  } {
    const now = Date.now();
    const attempts = this.attempts.get(identifier) || [];
    const recentAttempts = attempts.filter(time => now - time < this.DEFAULT_CONFIG.windowMs);
    
    return {
      isBlocked: this.isBlocked(identifier),
      remainingAttempts: this.getRemainingAttempts(identifier),
      timeUntilReset: this.getTimeUntilResetFormatted(identifier),
      attempts: recentAttempts.length
    };
  }

  /**
   * Clean up old entries (call periodically)
   */
  cleanup(): void {
    const now = Date.now();
    
    // Clean up expired blocks
    const blockedEntries = Array.from(this.blockedUntil.entries());
    for (const [identifier, blockedUntil] of blockedEntries) {
      if (now >= blockedUntil) {
        this.blockedUntil.delete(identifier);
        this.attempts.delete(identifier);
      }
    }
    
    // Clean up old attempts
    const attemptEntries = Array.from(this.attempts.entries());
    for (const [identifier, attempts] of attemptEntries) {
      const recentAttempts = attempts.filter((time: number) => now - time < this.DEFAULT_CONFIG.windowMs);
      
      if (recentAttempts.length === 0) {
        this.attempts.delete(identifier);
      } else {
        this.attempts.set(identifier, recentAttempts);
      }
    }
  }
}

export default RateLimiter;
